import React, { useMemo, useState } from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import Card from "../../Card";
import AccountEditForm from "./editFormsAcoounts/AccountEditForm";
import CredentialField from "./CredentialField";
import AccountSection from "./AccountSection";
import ValidCredentialsAlert from "../alerts/ValidCredentialAlert";
import { validateField } from "./validators";
import {
  PROVIDER_NAMES,
  PROVIDER_SCHEMAS,
  buildSubmitPayload,
  buildTestPayload,
  requiredFields,
} from "./providerSchemas";
import { dispatchSaveCredentials, dispatchTestConnection } from "./accountThunkMap";
import { updateNameNewAccount, resetStateCredentials } from "../../../store/slices/accounts/accountSlice";
import "../../../styles/accountForm.css";

const StatusChip = ({ kind, children }) => (
  <span className={`cco-chip cco-chip-${kind}`}>{children}</span>
);

const AccountForm = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { user } = useAuth0();

  const { idTenant } = useSelector((state) => state.costing);
  const { validCredentials, stateAccount, nameNewAccount, editAccount } = useSelector(
    (state) => state.accounts
  );

  const [provider, setProvider] = useState("AWS");
  const [values, setValues] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);

  const schema = PROVIDER_SCHEMAS[provider];
  // Antes del test no se piden los campos postTest: todavia no se pueden
  // completar y no aparecen en pantalla.
  const required = useMemo(
    () => (schema ? requiredFields(schema, { includePostTest: validCredentials === true }) : []),
    [schema, validCredentials]
  );

  if (editAccount) {
    return <AccountEditForm />;
  }

  const handleChange = (key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
    setTouched((current) => ({ ...current, [key]: true }));
  };

  const handleProviderChange = (event) => {
    const next = event.target.value;
    // Sin esto, un test exitoso de un provider dejaba al siguiente con
    // los campos bloqueados y vacíos.
    dispatch(resetStateCredentials());
    setValues({});
    setTouched({});
    setProvider(next);
  };

  const isEmpty = (field) => String(values[field.key] || "").trim() === "";
  const missing = required.filter(isEmpty);
  // En el alta hay que pasar el test antes de guardar; en la edicion no.
  const canSave = missing.length === 0 && validCredentials === true && !saving;

  const handleTest = () => {
    dispatch(
      dispatchTestConnection(dispatch, provider, buildTestPayload(schema, values), user.email)
    );
  };

  const handleSave = async () => {
    setSaving(true);
    const saved = await dispatchSaveCredentials(dispatch, provider, {
      data: buildSubmitPayload(schema, nameNewAccount, values),
      idTenant,
      user: user.email,
    });
    if (saved) history.push("/accounts");
    else setSaving(false);
  };

  const handleCancel = () => {
    dispatch(resetStateCredentials());
    history.push("/accounts");
  };

  const sectionStatus = (sectionFields) => {
    const pending = sectionFields.filter((field) => field.required && isEmpty(field)).length;
    if (pending > 0) return <StatusChip kind="warn">{pending} to complete</StatusChip>;
    return <StatusChip kind="ok">&#10003; Complete</StatusChip>;
  };

  return (
    <Card className="cco-account-form">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="header-title">
          <h4 className="card-title">New Account</h4>
        </div>
        <StatusChip kind="neutral">{schema.label}</StatusChip>
      </Card.Header>

      <Card.Body>
        <p style={{ fontSize: 13, color: "#5b5a72", marginBottom: 20 }}>
          Load your account data and credentials to access your consumption.
        </p>

        <ValidCredentialsAlert />

        <section className="cco-section">
          <AccountSection
            index={1}
            title="Identity"
            status={
              nameNewAccount.trim() ? (
                <StatusChip kind="ok">&#10003; Complete</StatusChip>
              ) : (
                <StatusChip kind="warn">1 to complete</StatusChip>
              )
            }
          />
          <div className="d-flex" style={{ gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <Form.Group className="form-group" controlId="cco-accountName">
                <Form.Label className="cco-label">
                  Account Name<span className="cco-required">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={nameNewAccount}
                  maxLength={25}
                  onChange={(event) => dispatch(updateNameNewAccount(event.target.value))}
                  placeholder="Enter Account Name"
                  style={{ borderColor: "#899AF1", color: "#000" }}
                />
                <div className="cco-hint">
                  Label shown across the dashboard. Max 25 characters.
                </div>
              </Form.Group>
            </div>
            <div style={{ width: 210 }}>
              <Form.Group className="form-group" controlId="cco-provider">
                <Form.Label className="cco-label">Cloud Provider</Form.Label>
                <Form.Select
                  value={provider}
                  disabled={validCredentials === true}
                  onChange={handleProviderChange}
                  style={{ borderColor: "#899AF1" }}
                >
                  {PROVIDER_NAMES.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Form.Select>
                <div className="cco-hint">
                  {validCredentials === true
                    ? "Locked until the connection test passes."
                    : "Changing it clears what you already typed."}
                </div>
              </Form.Group>
            </div>
          </div>
        </section>

        {schema.sections.map((section, position) => {
          // Las secciones postTest solo existen despues de validar.
          if (section.postTest && validCredentials !== true) return null;

          return (
            <section className="cco-section" key={section.id}>
              <AccountSection
                index={position + 2}
                title={section.title}
                status={sectionStatus(section.fields)}
              />

              {position === 0 && schema.credentialNote ? (
                <div className="cco-note" style={{ marginBottom: 16 }}>
                  <div className="cco-note-title">Why we ask for this</div>
                  {schema.credentialNote}
                </div>
              ) : null}

              <div className="cco-grid" style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
                {section.fields.map((field) => (
                  <CredentialField
                    key={field.key}
                    field={field}
                    value={values[field.key] || ""}
                    error={touched[field.key] ? validateField(field, values[field.key]) : null}
                    onChange={handleChange}
                    // Tras pasar el test las credenciales quedan bloqueadas,
                    // igual que antes: ya fueron validadas contra el provider.
                    disabled={validCredentials === true && !section.postTest}
                  />
                ))}
              </div>
            </section>
          );
        })}

        {validCredentials !== true ? (
          <div className="mb-2">
            <Button type="button" onClick={handleTest} variant="outline-success">
              {stateAccount === "ready" ? schema.testLabel : "Loading..."}
              {stateAccount !== "ready" && (
                <Spinner animation="grow" size="sm" className="ms-2" />
              )}
            </Button>
            <div className="cco-hint">
              Run the connection test before you can save. It checks the credentials against{" "}
              {schema.label} without storing anything.
            </div>
          </div>
        ) : null}
      </Card.Body>

      <div className="cco-actionbar">
        <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: "wrap" }}>
          <span className="cco-counter">
            {required.length - missing.length} of {required.length} required fields complete
          </span>
          {missing.length === 0 && validCredentials === true ? (
            <StatusChip kind="ok">&#10003; Ready to save</StatusChip>
          ) : null}
        </div>
        <div className="d-flex align-items-center" style={{ gap: 12 }}>
          <Button type="button" variant="link" className="cco-btn-ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button type="button" className="cco-btn-primary" disabled={!canSave} onClick={handleSave}>
            {saving ? "Saving..." : "Create account"}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default AccountForm;
